/**
 * v2 API contract of «Юрлица» (docs/research/design2-shell-capture.md §6, decisions-log V5): the
 * list of our legal entities and the entity card with its requisites, bank accounts, taxes,
 * numbering, template, projects and people.
 */
import { z } from 'zod';
import { isIsoDate } from '../dates';
import { CURRENCIES } from '../money';
import { NUMBERING_SCHEMES } from '../numbering';

export const LEGAL_ENTITY_TYPES = ['ip', 'llc', 'employment', 'other'] as const;
export type LegalEntityType = (typeof LEGAL_ENTITY_TYPES)[number];

/** Design `TYPES`: ИП / ООО / Трудоустройство / Другое. */
export const LEGAL_ENTITY_TYPE_LABELS: Readonly<Record<LegalEntityType, string>> = {
  ip: 'ИП',
  llc: 'ООО',
  employment: 'Трудоустройство',
  other: 'Другое',
};

/** Currencies a bank account can be opened in (design `curOpts`). */
export const ENTITY_BANK_CURRENCIES = CURRENCIES;
export type EntityBankCurrency = (typeof ENTITY_BANK_CURRENCIES)[number];

/** Numbering options of the «Нумерация» select. */
export const ENTITY_NUMBERING_LABELS: Readonly<Record<(typeof NUMBERING_SCHEMES)[number], string>> = {
  sequential_year: 'По порядку · 1060-2026',
  legacy_prefix: 'По дате · ABC-ддммгггг…',
};

/** Defaults of «+ Юрлицо» (design `onAdd`). */
export const NEW_ENTITY_NAME = 'Новое юрлицо';
export const NEW_ENTITY_COUNTRY = 'Грузия';

/** Free-text fields edited inline on the card. */
export const ENTITY_TEXT_FIELDS = [
  'name',
  'shortCode',
  'legalName',
  'country',
  'registrationNumber',
  'registrationDate',
  'taxId',
  'address',
  'director',
  'email',
  'phone',
  'taxRegime',
] as const;
export type EntityTextField = (typeof ENTITY_TEXT_FIELDS)[number];

/** Taxes on revenue (M4, V298) and employer tax rates (V15), in basis points. */
export const ENTITY_RATE_FIELDS = ['revenueTaxBp', 'revenueIncomeTaxBp', 'taxIncomeBp', 'taxPensionBp', 'taxEmployerBp'] as const;
export type EntityRateField = (typeof ENTITY_RATE_FIELDS)[number];

export type EntityField =
  EntityTextField | EntityRateField | 'entityType' | 'numberingScheme' | 'numberingNext' | 'laborRules';

/** Labour rules of salaries through the entity (V72): '' = none. */
export const ENTITY_LABOR_RULES = ['', 'BY'] as const;
export const ENTITY_LABOR_RULE_LABELS: Readonly<Record<(typeof ENTITY_LABOR_RULES)[number], string>> = {
  '': 'Не применяется',
  BY: 'ТК РБ: отпускные и больничные по среднему заработку',
};

/** Field labels as the card shows them; activity lines use them lower-cased. */
export const ENTITY_FIELD_LABELS: Readonly<Record<EntityField, string>> = {
  name: 'Название в приложении',
  shortCode: 'Код',
  legalName: 'Название на счёте',
  entityType: 'Тип',
  country: 'Страна',
  registrationNumber: 'Рег. номер',
  registrationDate: 'Дата регистрации',
  taxId: 'Налоговый номер',
  address: 'Юр. адрес',
  director: 'Директор',
  email: 'Email',
  phone: 'Телефон',
  taxRegime: 'Налоговый режим',
  revenueTaxBp: 'Налог с оборота, %',
  revenueIncomeTaxBp: 'Подоходный с выручки, %',
  taxIncomeBp: 'Подоходный, %',
  taxPensionBp: 'Пенсионный сотрудника, %',
  taxEmployerBp: 'Пенсионный работодателя, %',
  numberingScheme: 'Нумерация',
  numberingNext: 'Следующий номер',
  laborRules: 'Трудовое право',
};

export const ENTITY_SHORT_CODE_RE = /^[A-Z0-9]{1,4}$/;
export const ENTITY_TEXT_MAX = 500;
/** 100 % in basis points. */
export const ENTITY_RATE_MAX_BP = 10_000;
export const ENTITY_NUMBER_MAX = 999_999_999;

const textChange = z.object({
  field: z.enum(ENTITY_TEXT_FIELDS),
  value: z.string().trim().max(ENTITY_TEXT_MAX, 'Слишком длинное значение'),
});
const rateChange = z.object({
  field: z.enum(ENTITY_RATE_FIELDS),
  value: z.number().int().min(0).max(ENTITY_RATE_MAX_BP, 'Нужно число от 0 до 100').nullable(),
});

/** PATCH /entities/:id — one field per request (the card commits one field on blur). */
export const entityUpdateSchema = z.union([
  textChange,
  rateChange,
  z.object({ field: z.literal('entityType'), value: z.enum(LEGAL_ENTITY_TYPES) }),
  z.object({ field: z.literal('numberingScheme'), value: z.enum(NUMBERING_SCHEMES) }),
  z.object({ field: z.literal('numberingNext'), value: z.number().int().min(1).max(ENTITY_NUMBER_MAX) }),
  z.object({ field: z.literal('laborRules'), value: z.enum(ENTITY_LABOR_RULES) }),
  z.object({ field: z.literal('defaultTemplate'), value: z.string().trim().min(1).max(100) }),
  z.object({ field: z.literal('isArchived'), value: z.boolean() }),
]);
export type EntityUpdate = z.infer<typeof entityUpdateSchema>;

export const ENTITY_BANK_FIELDS = [
  'iban',
  'transitAccount',
  'bankName',
  'swift',
  'bankAddress',
  'intermediaryBankName',
  'intermediarySwift',
  'intermediaryAba',
] as const;
export type EntityBankField = (typeof ENTITY_BANK_FIELDS)[number];

/** Bank field labels (the invoice's own English captions, as in the design). */
export const ENTITY_BANK_LABELS: Readonly<Record<Exclude<EntityBankField, 'iban'>, string>> = {
  transitAccount: "Beneficiary's Transit Account",
  bankName: 'Beneficiary Bank',
  swift: "Beneficiary's Bank SWIFT",
  bankAddress: "Beneficiary's Bank Address",
  intermediaryBankName: 'Correspondent Bank',
  intermediarySwift: 'Correspondent Bank SWIFT',
  intermediaryAba: 'Correspondent Bank ABA',
};
export const entityIbanLabel = (currency: string): string => `Beneficiary's Account (${currency})`;

/** PATCH /entities/:id/banks/:bankId — one field, or the currency while the IBAN is empty. */
export const entityBankUpdateSchema = z.union([
  z.object({ field: z.enum(ENTITY_BANK_FIELDS), value: z.string().trim().max(ENTITY_TEXT_MAX) }),
  z.object({ field: z.literal('currency'), value: z.enum(ENTITY_BANK_CURRENCIES) }),
]);
export type EntityBankUpdate = z.infer<typeof entityBankUpdateSchema>;

/** POST /entities/:id/people — «+ Добавить человека» (sets `people.employer_issuer_id`). */
export const entityPersonSchema = z.object({ personId: z.string().min(1).max(64) });
export type EntityPersonInput = z.infer<typeof entityPersonSchema>;

/** What a dismissal pays as severance (ст. 48 ТК РБ, V292): nothing, or two weeks of the average earnings. */
export const SEVERANCE_OPTIONS = ['none', 'two_weeks'] as const;
export type SeveranceOption = (typeof SEVERANCE_OPTIONS)[number];
export const SEVERANCE_RU: Readonly<Record<SeveranceOption, string>> = { none: 'Нет', two_weeks: 'Двухнедельный средний заработок' };

/** POST /entities/:id/people/:personId/dismiss (V175, V292): the last working day at the entity and the severance. */
export const entityDismissSchema = z
  .object({ lastDay: z.string().refine(isIsoDate, 'Дата в формате ГГГГ-ММ-ДД'), severance: z.enum(SEVERANCE_OPTIONS).default('none') })
  .strict();
export type EntityDismissInput = z.infer<typeof entityDismissSchema>;

/** A YYYY-MM-DD registration date, or empty. */
export const isEntityDateValue = (value: string): boolean => value === '' || isIsoDate(value);

// ---------------------------------------------------------------- responses

export interface EntityListRow {
  readonly id: string;
  readonly name: string;
  /** «—» when not set. */
  readonly country: string;
  readonly type: string;
  /** Currencies of accounts with an IBAN, «USD · EUR», or «—». */
  readonly currencies: string;
  /** Projects issued by the entity that have not ended. */
  readonly projects: number;
  /** People on the entity who have not left. */
  readonly people: number;
  readonly isArchived: boolean;
}

/** GET /entities */
export interface EntitiesResponse {
  readonly items: readonly EntityListRow[];
}

export interface EntityBank {
  readonly id: string;
  readonly currency: string;
  readonly iban: string;
  readonly transitAccount: string;
  readonly bankName: string;
  readonly swift: string;
  readonly bankAddress: string;
  /** The correspondent (intermediary) bank a transfer in the currency goes through (V140); '' = none. */
  readonly intermediaryBankName: string;
  readonly intermediarySwift: string;
  readonly intermediaryAba: string;
}

/** A line of a card block: name, a muted sub line and a value on the right. */
export interface EntityBlockItem {
  readonly id: string;
  readonly title: string;
  readonly sub: string;
  readonly value: string;
}

export interface EntityOption {
  readonly id: string;
  readonly label: string;
}

export interface EntityTaxes {
  /** `YYYY-MM`: the first open month. */
  readonly period: string;
  /** Totals per currency, «553,84 ₾ · …». */
  readonly totals: string;
  readonly items: readonly EntityBlockItem[];
}

export interface EntityTemplate {
  readonly id: string;
  readonly name: string;
}

/** GET /entities/:id — text fields are '' when empty. */
export interface EntityCard {
  readonly id: string;
  readonly name: string;
  readonly shortCode: string;
  readonly legalName: string;
  readonly entityType: LegalEntityType;
  readonly country: string;
  readonly registrationNumber: string;
  readonly registrationDate: string;
  readonly taxId: string;
  readonly address: string;
  readonly director: string;
  readonly email: string;
  readonly phone: string;
  readonly taxRegime: string;
  /** Tax on revenue (M4); stored as `revenue_tax_rate_e6`. */
  readonly revenueTaxBp: number | null;
  /** The entity's own income tax on the revenue (V298); stored as `revenue_income_tax_rate_e6`. */
  readonly revenueIncomeTaxBp: number | null;
  /** Employer taxes on employees' pay (V15). */
  readonly taxIncomeBp: number | null;
  readonly taxPensionBp: number | null;
  readonly taxEmployerBp: number | null;
  /** Labour rules of salaries through the entity (V72); '' = none. */
  readonly laborRules: (typeof ENTITY_LABOR_RULES)[number];
  readonly numberingScheme: (typeof NUMBERING_SCHEMES)[number];
  readonly numberingNext: number;
  /** The number the next invoice would get; null for an employment entity. */
  readonly nextNumber: string | null;
  readonly defaultTemplate: string;
  /** Roles are implicit (V5): every non-employment entity issues invoices and employs people. */
  readonly canInvoice: boolean;
  readonly canEmploy: boolean;
  readonly isArchived: boolean;
  readonly banks: readonly EntityBank[];
  /** «Выставляет счета по проектам»: every project issued by the entity; `activeProjects` not ended. */
  readonly projects: readonly EntityBlockItem[];
  readonly activeProjects: number;
  /** «Оформлены на юрлицо»: people who have not left. */
  readonly people: readonly EntityBlockItem[];
  /** «+ Добавить человека»: people on another entity or none, not left and not candidates. */
  readonly peopleOptions: readonly EntityOption[];
  /** «Директор» picks from the team (V141): the names of the people who have not left, by name. */
  readonly directorOptions: readonly string[];
  /** «Налоги за {месяц}»; null when the entity does not employ. */
  readonly taxes: EntityTaxes | null;
  readonly templates: readonly EntityTemplate[];
}

/** Every entity write: the activity line and its undo token; creates also return the id. */
export interface EntityWriteResult {
  readonly message: string;
  readonly undoToken: string | null;
  readonly id?: string;
}
